import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useApiToast } from '../../hooks/useApiToast';
import './InvestmentModal.css';

const InvestmentModal = ({ isOpen, onClose, pool }) => {
  const { tokens } = useAuth();
  const { activeWallet } = useWallet();
  const { handleTokenOperation } = useApiToast();

  const [investmentAmount, setInvestmentAmount] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('amount'); // 'amount', 'confirm', 'processing', 'success'
  const [error, setError] = useState('');
  const [estimatedShares, setEstimatedShares] = useState(0);
  const [sharePrice, setSharePrice] = useState(1);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  useEffect(() => {
    if (isOpen && activeWallet) {
      fetchWalletBalance();
      fetchSharePrice();
    }
  }, [isOpen, activeWallet]);

  useEffect(() => {
    if (investmentAmount && sharePrice) {
      const amount = parseFloat(investmentAmount);
      if (!isNaN(amount) && amount > 0) {
        setEstimatedShares(amount / sharePrice);
      } else {
        setEstimatedShares(0);
      }
    }
  }, [investmentAmount, sharePrice]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchWalletBalance = async () => {
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${activeWallet.id}/balance`);
      if (data.success) {
        setWalletBalance(data.data.balance || 0);
      }
    } catch (error) {
      console.warn('Could not fetch wallet balance:', error.message);
    }
  };

  const fetchSharePrice = async () => {
    try {
      const data = await makeAuthenticatedRequest(`/investment/pools/${pool.id}/share-price`);
      if (data.success) {
        setSharePrice(data.data.sharePrice || 1);
      }
    } catch (error) {
      console.warn('Could not fetch share price:', error.message);
      setSharePrice(1); // Default to 1 SOL per share
    }
  };

  const validateInvestment = () => {
    const amount = parseFloat(investmentAmount);

    if (!amount || amount <= 0) {
      setError('Please enter a valid investment amount');
      return false;
    }

    if (amount < pool.min_investment) {
      setError(`Minimum investment is ${pool.min_investment} SOL`);
      return false;
    }

    if (pool.max_investment && amount > pool.max_investment) {
      setError(`Maximum investment is ${pool.max_investment} SOL`);
      return false;
    }

    if (amount > walletBalance) {
      setError('Insufficient wallet balance');
      return false;
    }

    if (amount > pool.available_size) {
      setError('Investment amount exceeds available pool capacity');
      return false;
    }

    setError('');
    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setInvestmentAmount(value);
    setError('');
  };

  const setMaxAmount = () => {
    const maxPossible = Math.min(
      walletBalance,
      pool.available_size,
      pool.max_investment || Infinity
    );
    setInvestmentAmount(maxPossible.toFixed(6));
  };

  const handleNext = () => {
    if (validateInvestment()) {
      setStep('confirm');
    }
  };

  const handleConfirm = async () => {
    if (!validateInvestment()) return;

    try {
      setLoading(true);
      setStep('processing');

      const investmentData = {
        pool_id: pool.id,
        wallet_id: activeWallet.id,
        amount: parseFloat(investmentAmount)
      };

      await handleTokenOperation(
        () => makeAuthenticatedRequest('/investment/invest', {
          method: 'POST',
          body: JSON.stringify(investmentData)
        }),
        {
          operationType: 'invest',
          successMessage: `Successfully invested ${investmentAmount} SOL in ${pool.name}!`
        }
      );

      setStep('success');

      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      setError(error.message);
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('amount');
    setInvestmentAmount('');
    setError('');
    setEstimatedShares(0);
    onClose();
  };

  const formatNumber = (num) => {
    const numValue = parseFloat(num);
    if (isNaN(numValue) || numValue === 0) return '0.000';
    return numValue.toFixed(3);
  };

  const formatPercentage = (percent) => {
    const percentValue = parseFloat(percent);
    if (isNaN(percentValue) || percentValue === 0) return '0.00%';
    const formatted = percentValue.toFixed(2);
    return `${percentValue >= 0 ? '+' : ''}${formatted}%`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="investment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {step === 'amount' && 'Invest in Pool'}
            {step === 'confirm' && 'Confirm Investment'}
            {step === 'processing' && 'Processing Investment'}
            {step === 'success' && 'Investment Successful'}
          </h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Pool Information */}
          <div className="pool-info-summary">
            <h4>{pool.name}</h4>
            <div className="pool-metrics-summary">
              <div className="metric">
                <span className="label">30d Return:</span>
                <span className={`value ${pool.return_30d >= 0 ? 'positive' : 'negative'}`}>
                  {formatPercentage(pool.return_30d || 0)}
                </span>
              </div>
              <div className="metric">
                <span className="label">Management Fee:</span>
                <span className="value">{pool.management_fee}%</span>
              </div>
              <div className="metric">
                <span className="label">Performance Fee:</span>
                <span className="value">{pool.performance_fee}%</span>
              </div>
            </div>
          </div>

          {/* Step: Amount Selection */}
          {step === 'amount' && (
            <div className="amount-step">
              <div className="wallet-info">
                <div className="balance-display">
                  <span className="balance-label">Available Balance:</span>
                  <span className="balance-value">{formatNumber(walletBalance)} SOL</span>
                </div>
              </div>

              <div className="amount-input-section">
                <label htmlFor="investment-amount">Investment Amount (SOL)</label>
                <div className="amount-input-container">
                  <input
                    id="investment-amount"
                    type="number"
                    value={investmentAmount}
                    onChange={handleAmountChange}
                    placeholder="0.000"
                    min={pool.min_investment}
                    max={Math.min(walletBalance, pool.available_size, pool.max_investment || Infinity)}
                    step="0.001"
                    className="amount-input"
                  />
                  <button
                    className="max-btn"
                    onClick={setMaxAmount}
                    type="button"
                  >
                    MAX
                  </button>
                </div>

                <div className="amount-constraints">
                  <div className="constraint">
                    <span className="constraint-label">Minimum:</span>
                    <span className="constraint-value">{formatNumber(pool.min_investment)} SOL</span>
                  </div>
                  {pool.max_investment && (
                    <div className="constraint">
                      <span className="constraint-label">Maximum:</span>
                      <span className="constraint-value">{formatNumber(pool.max_investment)} SOL</span>
                    </div>
                  )}
                  <div className="constraint">
                    <span className="constraint-label">Available in Pool:</span>
                    <span className="constraint-value">{formatNumber(pool.available_size)} SOL</span>
                  </div>
                </div>
              </div>

              {investmentAmount && estimatedShares > 0 && (
                <div className="investment-preview">
                  <h5>Investment Preview</h5>
                  <div className="preview-metrics">
                    <div className="preview-metric">
                      <span className="label">Investment Amount:</span>
                      <span className="value">{formatNumber(parseFloat(investmentAmount))} SOL</span>
                    </div>
                    <div className="preview-metric">
                      <span className="label">Share Price:</span>
                      <span className="value">{formatNumber(sharePrice)} SOL</span>
                    </div>
                    <div className="preview-metric">
                      <span className="label">Estimated Shares:</span>
                      <span className="value">{formatNumber(estimatedShares)}</span>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step: Confirmation */}
          {step === 'confirm' && (
            <div className="confirm-step">
              <div className="confirmation-details">
                <h5>Please confirm your investment:</h5>

                <div className="confirmation-summary">
                  <div className="summary-row">
                    <span className="label">Pool:</span>
                    <span className="value">{pool.name}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Investment Amount:</span>
                    <span className="value">{formatNumber(parseFloat(investmentAmount))} SOL</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Share Price:</span>
                    <span className="value">{formatNumber(sharePrice)} SOL per share</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Shares to Receive:</span>
                    <span className="value">{formatNumber(estimatedShares)}</span>
                  </div>
                </div>

                <div className="fee-breakdown">
                  <h6>Fee Structure:</h6>
                  <div className="fee-item">
                    <span className="fee-label">Management Fee:</span>
                    <span className="fee-value">{pool.management_fee}% annually</span>
                  </div>
                  <div className="fee-item">
                    <span className="fee-label">Performance Fee:</span>
                    <span className="fee-value">{pool.performance_fee}% of profits</span>
                  </div>
                </div>

                {pool.withdrawal_notice_days > 0 && (
                  <div className="withdrawal-notice">
                    <span className="notice-icon">ℹ️</span>
                    <span className="notice-text">
                      Note: {pool.withdrawal_notice_days} day notice required for withdrawals
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="processing-step">
              <div className="processing-animation">
                <div className="loading-spinner"></div>
                <h5>Processing Your Investment</h5>
                <p>Please wait while we process your investment...</p>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="success-step">
              <div className="success-animation">
                <div className="success-icon">✅</div>
                <h5>Investment Successful!</h5>
                <p>Your investment of {formatNumber(parseFloat(investmentAmount))} SOL has been processed.</p>
                <p>You have received {formatNumber(estimatedShares)} shares in {pool.name}.</p>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {step === 'amount' && (
            <>
              <button className="cancel-btn" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="next-btn primary"
                onClick={handleNext}
                disabled={!investmentAmount || parseFloat(investmentAmount) <= 0}
              >
                Next
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button className="back-btn" onClick={() => setStep('amount')}>
                Back
              </button>
              <button
                className="confirm-btn primary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Investment'}
              </button>
            </>
          )}

          {step === 'processing' && (
            <button className="processing-btn" disabled>
              Processing...
            </button>
          )}

          {step === 'success' && (
            <button className="close-btn primary" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentModal;